import { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query'; 
import { getCurrentUser } from '../api/AUTH/auth';
import { getRoles } from '../api/ROLES/roles';

const AuthContext = createContext();

// AuthProvider component to wrap the app and provide authentication context
export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Check if there's cached user data on mount
    const cachedUser = queryClient.getQueryData(['currentUser']);
    return !!cachedUser?.data;
  });

  // Fetch current user data using React Query
  const {
    data: user,
    refetch: fetchUser,
    isLoading: loading,
    isError,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24h
    retry: 1,
    retryDelay: 1000,
    enabled: true,
  });

  // Fetch all roles and permissions
  const {
    data: rolesData,
    isLoading: rolesLoading,
  } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24h
    retry: 1,
    retryDelay: 1000,
    enabled: !!user?.data, // Only fetch roles if user is authenticated
  });

  // Essential for storing user info after page refresh
  useEffect(() => {
    if (user?.data) {
      setIsLoggedIn(true);
    } else if (isError && !loading) {
      // Only set to false if there's an error AND not loading
      // This prevents premature logout during initial load
      setIsLoggedIn(false);
    }
  }, [user, isError, loading]);

  // Get user permissions based on their role
  const getUserPermissions = () => {
    if (!user?.data?.role || !rolesData?.data) return [];
    
    const userRole = rolesData.data.find(role => role.id === user.data.role.id);
    return userRole?.permissions || [];
  };

  // Helper to check if user has a specific role
  const hasRole = (roleName) => {
    return user?.data?.role?.name === roleName;
  };

  // Helper to check if user has multiple roles (OR condition)
  const hasAnyRole = (roleNames) => {
    return roleNames.some(roleName => hasRole(roleName));
  };

  // Helper to check if user is admin
  const isAdmin = () => hasRole('ADMIN');

  // Helper to check if user has a specific permission
  const hasPermission = (permissionName) => {
    const permissions = getUserPermissions();
    return permissions.some(perm => perm.name === permissionName && perm.is_active);
  };

  // Helper to check if user has any of multiple permissions (OR condition)
  const hasAnyPermission = (permissionNames) => {
    return permissionNames.some(permName => hasPermission(permName));
  };

  // Helper to check if user has all of multiple permissions (AND condition)
  const hasAllPermissions = (permissionNames) => {
    return permissionNames.every(permName => hasPermission(permName));
  };

  // Function to log out the user
  const logOut = () => {
    setIsLoggedIn(false);
    queryClient.setQueryData(['currentUser'], null); 
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: loading || rolesLoading,
        isError,
        fetchUser,
        logOut,
        isLoggedIn,
        setIsLoggedIn,
        hasRole,
        hasAnyRole,
        isAdmin,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getUserPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
